<template>
  <HeaderBar />
  <section class="carvia-container flex flex-col justify-center">
    <div class="flex-auto">
      <div class="mb-7">
        <div class="grid flex-row gap-4 space-y-0 md:flex md:items-center">
          <h2 class="w-full max-md:hidden">
            {{ t('upcomingHandovers') }}
          </h2>
          <StationSwitcher
            class="basis-2/5 text-primary md:max-w-[200px]"
            use-station-store
            has-null-option
            :null-option-title="t('myStations')"
          />
        </div>

        <div class="flex flex-wrap-reverse items-end lg:flex-nowrap lg:justify-between">
          <Tabs
            v-model="activeTab"
            :tabs="tabs"
            :default="Tab.ALL"
            class="w-full md:col-span-3 lg:w-auto"
          />

          <div class="lg-mt-0 mt-3 flex w-full items-end justify-end gap-2 lg:w-auto">
            <template v-if="showRangePicker">
              <DateRangeFilterPicker
                :start-date="filterDateFrom"
                :end-date="filterDateFrom ? filterDateTo : null"
                :placeholder="t('dateRange')"
                :auto-open="rangePickerOpen"
                class="w-full min-w-[13rem] max-w-[16rem]"
                data-testid="handover-datepicker"
                @change="handleRangeChange"
              />
              <CVButton variant="secondary" @click="resetToDefault">{{ t('today') }}</CVButton>
            </template>
            <template v-else>
              <CVButton variant="secondary" @click="rangePickerOpen = true">
                {{ t('changeDateRange') }}
              </CVButton>
              <CVButton variant="secondary" @click="setTomorrow">{{ t('tomorrow') }}</CVButton>
            </template>
          </div>
        </div>
      </div>

      <AllocationTransactionList
        v-if="[Tab.ALL, Tab.PICKUPS, Tab.RETURNS].includes(activeTab)"
        :filter-transaction-types="filterTransactionTypes"
        :filter-date-from="filterDateFrom"
        :filter-date-to="filterDateTo"
      />

      <DetailingList
        v-else
        :key="activeTab"
        :mode="activeTab === Tab.DETAILING ? 'detailing' : 'fleetIn'"
      />
    </div>
  </section>
</template>

<script lang="ts" setup>
import HeaderBar from '@/components/headerbar/HeaderBar.vue';
import { useI18n } from 'vue-i18n';
import { useUiStore } from '@/stores/ui.store';
import AllocationTransactionList from '@/views/handover/components/allocation-transaction-list/AllocationTransactionList.vue';
import { DateTime } from 'luxon';
import { computed, ref, watchEffect } from 'vue';
import StationSwitcher from '../settings/components/StationSwitcher.vue';
import DetailingList from '@/views/handover/components/detailing-list/DetailingList.vue';
import Tabs, { type TabItem } from '@/components/Tabs.vue';
import DateRangeFilterPicker from '@/components/DateRangeFilterPicker.vue';
import { useRouteQueryRef } from '@/hooks/use-route-query-ref';
import { AllocationTransactionType } from '@carvia/ros-client-types';
import { FindAllCarsSort } from '@/entities/cars/find-all-cars-sort.enum';
import { Order } from '@/entities/pagination/order.enum';
import { CarStatus } from '@/entities/cars/car-status.enum';
import { useCarsWithQuery } from '@/queries/use-cars';
import { useCurrentStations } from '@/hooks/use-current-stations';
import { useFlattenPaginatedData } from '@/hooks/use-flatten-paginated-data';

enum Tab {
  PICKUPS = 'PICKUPS',
  RETURNS = 'RETURNS',
  ALL = 'ALL',
  DETAILING = 'DETAILING',
  FLEET_IN = 'FLEET_IN',
}

const { t } = useI18n();
const uiStore = useUiStore();

watchEffect(() => {
  uiStore.setHeaderTitle(t('handovers'));
});

const filterDateFrom = useRouteQueryRef('dateFrom', null, 'date');
const filterDateTo = useRouteQueryRef('dateTo', null, 'date');
const rangePickerOpen = ref(false);

const isDefaultFilter = computed(() => !filterDateFrom.value && !filterDateTo.value);

const showRangePicker = computed(() => rangePickerOpen.value || !isDefaultFilter.value);

const resetToDefault = () => {
  filterDateFrom.value = null;
  filterDateTo.value = null;
  rangePickerOpen.value = false;
};

const setTomorrow = () => {
  const tomorrow = DateTime.now().plus({ days: 1 }).startOf('day').toJSDate();
  filterDateFrom.value = tomorrow;
  filterDateTo.value = new Date(tomorrow);
};

const handleRangeChange = (value: { startDate: Date | null; endDate: Date | null }) => {
  if (!value.startDate || !value.endDate) {
    resetToDefault();
    return;
  }
  filterDateFrom.value = value.startDate;
  filterDateTo.value = value.endDate;
};

const mapTabToStatus = (tab?: Tab | null) => {
  switch (tab) {
    case Tab.PICKUPS:
      return [AllocationTransactionType.HANDOVER];
    case Tab.RETURNS:
      return [AllocationTransactionType.RETURN];
    default:
      return [AllocationTransactionType.HANDOVER, AllocationTransactionType.RETURN];
  }
};

const activeTab = ref<Tab>(Tab.ALL);
const filterTransactionTypes = ref(mapTabToStatus(activeTab.value));

watchEffect(() => {
  if (activeTab.value === Tab.DETAILING || activeTab.value === Tab.FLEET_IN) return;
  filterTransactionTypes.value = mapTabToStatus(activeTab.value);
});

const currentStations = useCurrentStations();

const getCarsQuery = computed(() => ({
  stationIds: currentStations.value,
  sort: [FindAllCarsSort.NEXT_ALLOCATION_START_DATE],
  order: Order.ASC,
  includeNextAllocationStartDate: true,
  status: [CarStatus.ORDERED],
  hasNextAllocationStartDate: true,
  isSetregistrationAndOrganizationDoneDate: true,
}));

const { data: fleetInCarsData } = useCarsWithQuery(getCarsQuery);
const fleetInCars = useFlattenPaginatedData(fleetInCarsData);

const urgentFleetInsBadge = computed(() => {
  const number = fleetInCars.value.filter((car) => {
    const isToday =
      car.nextAllocationStartDate &&
      DateTime.fromISO(car.nextAllocationStartDate).startOf('day') <= DateTime.now().startOf('day');
    return isToday;
  }).length;
  return number ? (number > 9 ? '9+' : number.toString()) : undefined;
});

const tabs = computed<TabItem[]>(() => [
  {
    title: t('all'),
    id: Tab.ALL,
  },
  {
    title: t('pickups'),
    id: Tab.PICKUPS,
  },
  {
    title: t('returns'),
    id: Tab.RETURNS,
  },
  {
    title: t('detailing'),
    id: Tab.DETAILING,
  },
  {
    title: t('fleetIn'),
    id: Tab.FLEET_IN,
    badge: urgentFleetInsBadge.value,
  },
]);
</script>

<i18n lang="json">
{
  "en": {
    "handovers": "Handovers",
    "upcomingHandovers": "Upcoming handovers",
    "all": "All",
    "pickups": "Pickups",
    "returns": "Returns",
    "detailing": "Detailing",
    "fleetIn": "Fleet In",
    "today": "Today",
    "tomorrow": "Tomorrow",
    "changeDateRange": "Change date range",
    "dateRange": "Date range",
    "myStations": "My Stations"
  },
  "de": {
    "handovers": "Übergaben / Rückgaben",
    "upcomingHandovers": "Anstehende Übergaben & Rückgaben",
    "all": "Alles",
    "pickups": "Übergaben",
    "returns": "Rückgaben",
    "detailing": "Aufbereiten",
    "fleetIn": "Einflotten",
    "today": "Heute",
    "tomorrow": "Morgen",
    "changeDateRange": "Zeitraum ändern",
    "dateRange": "Zeitraum",
    "myStations": "Meine Stationen"
  },
  "es": {
    "handovers": "Entregas / devoluciones",
    "upcomingHandovers": "Próximas entregas y devoluciones",
    "all": "Todo",
    "pickups": "Entregas",
    "returns": "Devoluciones",
    "detailing": "Detailing",
    "fleetIn": "Entrada a flota",
    "today": "Hoy",
    "tomorrow": "Mañana",
    "changeDateRange": "Cambiar periodo",
    "dateRange": "Periodo",
    "myStations": "Mis estaciones"
  }
}
</i18n>
